import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Calculator, Target, TrendingUp, AlertCircle } from 'lucide-react';

const MaterialityCalculator = () => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCalculator, setShowCalculator] = useState(false);
  const [formData, setFormData] = useState({
    indicator_id: '',
    indicator_name: '',
    category: 'E1',
    impact_severity: 3,
    impact_scope: 3,
    impact_likelihood: 3,
    financial_magnitude: 3,
    financial_likelihood: 3,
    financial_timeframe: 3,
    justification: '',
    assessed_by: 'ESG Team'
  });
  const [calculatedResult, setCalculatedResult] = useState(null);

  const fetchAssessments = async () => {
    const data = await apiFetch(`${API}/materiality`);
    if (data?._error) {
      console.error('Error fetching assessments:', data.message);
      setLoading(false);
      return;
    }
    setAssessments(data);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchAssessments();
  }, []);

  const calculateMateriality = async () => {
    const data = await apiFetch(`${API}/materiality/calculate`, { method: 'POST', body: formData });
    if (data?._error) {
      console.error('Error calculating materiality:', data.message);
      return;
    }
    setCalculatedResult(data);
  };

  const saveAssessment = async () => {
    const data = await apiFetch(`${API}/materiality`, { method: 'POST', body: formData });
    if (data?._error) {
      console.error('Error saving assessment:', data.message);
      return;
    }
    fetchAssessments();
    setShowCalculator(false);
    setCalculatedResult(null);
  };

  const classificationColors = {
    'Double Material': 'bg-red-100 text-red-800 border-red-600',
    'Impact Material': 'bg-orange-100 text-orange-800 border-orange-600',
    'Financial Material': 'bg-blue-100 text-blue-800 border-blue-600',
    'Not Material': 'bg-slate-100 text-slate-800 border-slate-600'
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="materiality-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Double Materiality Assessment</h1>
          <p className="text-slate-600">Impact & Financial materiality scoring per ESRS</p>
        </div>
        <button
          onClick={() => setShowCalculator(!showCalculator)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          data-testid="new-assessment-btn"
        >
          <Calculator size={20} />
          New Assessment
        </button>
      </div>

      {/* Calculator Form */}
      {showCalculator && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Materiality Calculator</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Indicator Name</label>
              <input
                type="text"
                value={formData.indicator_name}
                onChange={(e) => setFormData({...formData, indicator_name: e.target.value})}
                className="w-full border border-slate-300 rounded px-3 py-2"
                placeholder="e.g., GHG Scope 1 Emissions"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full border border-slate-300 rounded px-3 py-2"
              >
                <option value="E1">E1 - Climate Change</option>
                <option value="E2">E2 - Pollution</option>
                <option value="E3">E3 - Water</option>
                <option value="E4">E4 - Biodiversity</option>
                <option value="E5">E5 - Circular Economy</option>
                <option value="S1">S1 - Own Workforce</option>
                <option value="S2">S2 - Value Chain</option>
                <option value="S3">S3 - Communities</option>
                <option value="S4">S4 - Consumers</option>
                <option value="G1">G1 - Business Conduct</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Impact Materiality</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Severity (1-5)</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={formData.impact_severity}
                    onChange={(e) => setFormData({...formData, impact_severity: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <span className="text-sm text-slate-600">Value: {formData.impact_severity}</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Scope (1-5)</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={formData.impact_scope}
                    onChange={(e) => setFormData({...formData, impact_scope: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <span className="text-sm text-slate-600">Value: {formData.impact_scope}</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Likelihood (1-5)</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={formData.impact_likelihood}
                    onChange={(e) => setFormData({...formData, impact_likelihood: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <span className="text-sm text-slate-600">Value: {formData.impact_likelihood}</span>
                </div>
              </div>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Financial Materiality</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Magnitude (1-5)</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={formData.financial_magnitude}
                    onChange={(e) => setFormData({...formData, financial_magnitude: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <span className="text-sm text-slate-600">Value: {formData.financial_magnitude}</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Likelihood (1-5)</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={formData.financial_likelihood}
                    onChange={(e) => setFormData({...formData, financial_likelihood: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <span className="text-sm text-slate-600">Value: {formData.financial_likelihood}</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Timeframe (1-5)</label>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={formData.financial_timeframe}
                    onChange={(e) => setFormData({...formData, financial_timeframe: parseInt(e.target.value)})}
                    className="w-full"
                  />
                  <span className="text-sm text-slate-600">Value: {formData.financial_timeframe}</span>
                </div>
              </div>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="text-lg font-semibold text-slate-900 mb-3">Result</h3>
              {calculatedResult && (
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-slate-700">Impact Score</span>
                    <div className="text-2xl font-bold text-orange-600">{calculatedResult.impact_score.toFixed(1)}%</div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-700">Financial Score</span>
                    <div className="text-2xl font-bold text-blue-600">{calculatedResult.financial_score.toFixed(1)}%</div>
                  </div>
                  <div>
                    <span className={`px-3 py-1 text-sm font-semibold rounded border-2 inline-block ${classificationColors[calculatedResult.double_materiality_classification]}`}>
                      {calculatedResult.double_materiality_classification}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">Justification</label>
            <textarea
              value={formData.justification}
              onChange={(e) => setFormData({...formData, justification: e.target.value})}
              className="w-full border border-slate-300 rounded px-3 py-2"
              rows="3"
              placeholder="Explain the materiality assessment rationale..."
            ></textarea>
          </div>

          <div className="flex gap-3">
            <button
              onClick={calculateMateriality}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              data-testid="calculate-btn"
            >
              <Calculator size={18} />
              Calculate
            </button>
            {calculatedResult && (
              <button
                onClick={saveAssessment}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                data-testid="save-assessment-btn"
              >
                Save Assessment
              </button>
            )}
            <button
              onClick={() => { setShowCalculator(false); setCalculatedResult(null); }}
              className="px-4 py-2 bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Assessments List */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-slate-900">Existing Assessments ({assessments.length})</h2>
        {assessments.map((assessment) => (
          <div key={assessment.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{assessment.indicator_name}</h3>
                <span className="text-sm text-slate-600">{assessment.category}</span>
              </div>
              <span className={`px-3 py-1 text-sm font-semibold rounded border-2 ${classificationColors[assessment.double_materiality_classification]}`}>
                {assessment.double_materiality_classification}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <span className="text-sm font-medium text-slate-600">Impact Score</span>
                <div className="text-2xl font-bold text-orange-600">{assessment.impact_score.toFixed(1)}%</div>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Financial Score</span>
                <div className="text-2xl font-bold text-blue-600">{assessment.financial_score.toFixed(1)}%</div>
              </div>
            </div>

            <div className="bg-slate-50 rounded p-3">
              <span className="text-xs font-semibold text-slate-600 uppercase">Justification</span>
              <p className="text-sm text-slate-700 mt-1">{assessment.justification}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MaterialityCalculator;